import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

/**
 * Lightweight headless dropdown
 * Props
 * • options : [{ value:string, label:string }]
 * • value   : current value
 * • onChange(val)
 * • label   : optional text label
 */
export default function Dropdown({
  label,
  options = [],
  value,
  onChange,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // ► close on outside click
  useEffect(() => {
    function handler(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value) || {
    label: 'Select…',
  };

  return (
    <div ref={wrapperRef} className={clsx('relative', className)}>
      {label && (
        <label className="mb-1 block text-sm font-medium text-charcoal">
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded border border-light-gray px-3 py-2 text-left"
      >
        <span>{selected.label}</span>
        <ChevronDownIcon className="h-5 w-5 text-slate-gray" />
      </button>

      {/* Listbox */}
      {open && (
        <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded border border-light-gray bg-white shadow-md">
          {options.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={clsx(
                  'w-full px-3 py-2 text-left hover:bg-very-light-gray',
                  opt.value === value && 'bg-soft-yellow',
                )}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
